import React, { useMemo, useState, useEffect } from "react"
import { Link, useHistory, useLocation } from "react-router-dom"
import { LockOutlined, UserOutlined, LeftOutlined } from "@ant-design/icons"
import { message, Button, Input, Card, Row, Col, Typography, Form, Checkbox, Space } from "antd"
import { useStoreActions, useStoreState, StateMapper } from "easy-peasy"
import { AppDataStore } from "../appData/types"
import CookiePermissionModal from "./CookiePermissionModal"
import s_clubhouse from "../images/S_Clubhouse.png"
import {
  gen,
  IUser,
  getLandingPageRoute,
  swimminglyApi,
} from "./utils"
import { seasonPrepModalListener } from "../App"
import { faPersonSwimmingWater } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import PasswordReset from "./PasswordReset"

interface LocationState {
  token?: string | null
  targetUrl?: string | null
}

export default function Login(props: { targetURL?: string | undefined; clubToken?: string | undefined }): JSX.Element {
  const location = useLocation<LocationState>()
  const token = props.clubToken ?? location?.state?.token
  const targetUrl = props.targetURL ?? location?.state?.targetUrl
  const [email, setEmail] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const [cookieModalVisible, setCookieModalVisible] = useState(false)
  const [loading, setLoading] = useState<boolean>(false)

  // view state: 'login' or 'reset'
  const [authView, setAuthView] = useState<"login" | "reset">(location.pathname === "/passwordReset" ? "reset" : "login")

  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const acceptedCookies = useStoreState((state: StateMapper<AppDataStore>) => state.acceptedCookies)
  const setAcceptedCookies = useStoreActions((actions: AppDataStore) => actions.setAcceptedCookies)
  const setAuthUser = useStoreActions((actions: AppDataStore) => actions.setAuthUser)
  const setUser = useStoreActions((actions: AppDataStore) => actions.setUser)
  const setAliasedUser = useStoreActions((actions: AppDataStore) => actions.setAliasedUser)
  const setImpersonateClub = useStoreActions((actions: AppDataStore) => actions.setAliasedClub)

  const history = useHistory()
  const currentYear = useMemo(() => new Date().getFullYear(), [])

  useEffect(() => {
    // if route changes externally, keep authView in sync
    if (location.pathname === "/passwordReset") setAuthView("reset")
    if (location.pathname !== "/passwordReset" && authView === "reset") setAuthView("login")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    let mounted = true
    if (user && acceptedCookies) {
      const destination = targetUrl || getLandingPageRoute(user)
      if (mounted) history.push(destination)
    } else if (!acceptedCookies) {
      if (mounted) setCookieModalVisible(true)
    } else {
      if (mounted) setCookieModalVisible(false)
    }
    return () => {
      mounted = false
    }
  }, [user, acceptedCookies, history, targetUrl])

  const acceptCookies = () => {
    fetch(gen("/api/makeCookieChoice"), {
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      credentials: "same-origin",
      method: "post",
      body: JSON.stringify({ cookieChoice: true }),
    })
      .then((response: Response) => response.json())
      .then((data) => {
        if (data.status === "success") {
          setAcceptedCookies(true)
          setCookieModalVisible(false)
        }
      })
  }

  const onClick = async () => {
    if (!acceptedCookies) {
      setCookieModalVisible(true)
      return
    }
    setLoading(true)
    const responseData = await swimminglyApi.post(gen("/api/login")).body({ email, password })

    // If the backend returned a club (typical for club admins / parents), use it
    if (responseData && responseData.club) {
      setImpersonateClub(responseData.club)
      if (responseData.user) {
        swimminglyApi
          .post(gen(`/api/updateAliasedClub/${responseData.user.id || responseData.user.userId}`))
          .body({ clubId: responseData.club.clubId })
      }
    }

    if (responseData && (responseData.error || responseData.status === "error")) {
      message.error("Failed credentials")
      setLoading(false)
      return
    }
    if (responseData && responseData.exception) {
      message.error("Server error")
      setLoading(false)
      return
    }

    if (responseData && responseData.user) {
      await new Promise((resolve) => {
        const loginUser: IUser = responseData.user
        setAuthUser(loginUser)
        setUser(loginUser)
        setAliasedUser(null)
        setLoading(false)
        resolve(true)
      })
        .then(async () => {
          // For roles like league admin (3) or Swimmingly admin (2), /api/login
          // does not include a club in the payload. To avoid a blank
          // "Logged in as" state until the user visits a route that
          // back-fills aliasedClub, ask whoami for the resolved club
          // (which prefers lastAliasedClub and falls back to users.club_id
          // or league_id) and seed aliasedClub from that.
          if (!responseData.club) {
            try {
              const whoami = await swimminglyApi.get(gen("/api/whoami"))
              if (whoami && whoami.club) {
                setImpersonateClub(whoami.club)
                if (whoami.user) {
                  swimminglyApi
                    .post(gen(`/api/updateAliasedClub/${whoami.user.id || whoami.user.userId}`))
                    .body({ clubId: whoami.club.clubId })
                }
              }
            } catch (e) {
              // If this fails, we fall back to existing behavior where
              // aliasedClub will be populated later by NewPrivateRoute.
            }
          }
          return 1
        })
        .then(() => {
          setTimeout(() => seasonPrepModalListener.emit("updateSeasonPrepModal"), 50)
          return 1
        })
        .then(() => {
          setTimeout(() => {
            if (token) {
              history.push({ pathname: `/clubs/register/${token}`, state: { justLoggedIn: true } })
            } else if (targetUrl) {
              history.push({ pathname: targetUrl, state: { justLoggedIn: true } })
            } else {
              history.push({ pathname: "/app/landingPage", state: { justLoggedIn: true } })
            }
          }, 50)
        })
      return true
    }
  }

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") onClick()
  }

  // open embedded reset panel; prefill email if present, push URL
  const openReset = (prefill?: string) => {
    if (prefill) setEmail(prefill)
    // set view first so transforms are applied, then update URL on next paint
    setAuthView("reset")
    requestAnimationFrame(() => {
      history.push("/passwordReset")
    })
  }

   const backToLogin = () => {
    // switch view first so the slide animation runs
    setAuthView("login")
    // wait a frame, then wait for the transition to finish before changing the URL
    requestAnimationFrame(() => {
      window.setTimeout(() => {
        history.push("/login")
      }, 360) // match/just-above CSS transition duration (350ms)
    })
  }

  // called when embedded PasswordReset reports success
  const onResetSuccess = () => {
    setAuthView("login")
    // give the slide-back animation time to play, then update the URL and show the message
    requestAnimationFrame(() => {
      window.setTimeout(() => {
        history.push("/login")
        message.success(
          "If that email address has a Swimmingly Clubhouse account, an password reset email has been sent. Please check your inbox & follow instructions"
        )
      }, 360)
    })
  }

  // brand colors from guide
  const navy = "#101242"
  const blue = "#25bfea"

  return (
    <>
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: `linear-gradient(180deg, ${blue}10 0%, #eef5ff 100%)`,
          backgroundImage: `linear-gradient(180deg, rgba(37,191,234,0.06), rgba(238,245,255,1))`,
        }}
      >
        <Card
          style={{
            width: "100%",
            maxWidth: 1024,
            borderRadius: 12,
            boxShadow: "0 12px 30px rgba(16,18,66,0.12)",
            overflow: "hidden",
          }}
        >
          <Row gutter={24} align="middle">
            <Col xs={24} md={10} style={{ display: "flex", justifyContent: "center", padding: 24, background: "linear-gradient(180deg,#ffffff 0%, rgba(141,226,245,0.08) 100%)" }}>
              <div style={{ textAlign: "center", width: "100%" }}>
                <Link to="/">
                  <img src={s_clubhouse} alt="Swimmingly Clubhouse" style={{ width: 320, maxWidth: "100%", height: "auto", marginBottom: 12 }} />
                </Link>
                <div style={{ fontSize: 28, color: navy, fontWeight: 700, display: "flex", alignItems: "center", gap: 10, justifyContent: "center", marginTop: 8 }}>
                  <span style={{ fontSize: 24, color: navy, fontWeight: 700, opacity: 0.7 }}>Welcome Back</span>
                </div>
                <Typography.Text type="secondary">The sport's one-stop-shop.</Typography.Text>
              </div>
            </Col>

            <Col xs={24} md={14}>
              {/* Sliding container: left = login form, right = reset form */}
              <div style={{ maxWidth: 420, margin: "0 auto", padding: 18 }}>
                <div style={{ position: "relative", overflow: "hidden", height: 420 /* adjust as needed */, width: "100%" }}>
                  {/* Login panel */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transition: "transform 350ms cubic-bezier(.2,.8,.2,1)",
                      willChange: "transform",
                      backfaceVisibility: "hidden",
                      transform: authView === "login" ? "translateX(0%)" : "translateX(-100%)",
                    }}
                  >
                    <Form layout="vertical" onFinish={onClick} initialValues={{ remember: true }}>
                      <Form.Item name="email" label="Email" rules={[{ required: true, message: "Please enter your email" }]}>
                        <Input
                          size="large"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          prefix={<UserOutlined />}
                          autoFocus
                        />
                      </Form.Item>

                      <Form.Item name="password" label="Password" rules={[{ required: true, message: "Please enter your password" }]}>
                        <Input.Password
                          size="large"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          prefix={<LockOutlined />}
                          onKeyDown={handleKeyPress}
                        />
                      </Form.Item>

                      <Form.Item>
                        <Row justify="space-between" align="middle">
                          <Col>
                            <Checkbox>Remember me</Checkbox>
                          </Col>
                          <Col>
                            <a onClick={() => openReset(email)} style={{ cursor: "pointer" }}>
                              Forgot password?
                            </a>
                          </Col>
                        </Row>
                      </Form.Item>

                      <Form.Item>
                        <Space orientation="vertical" style={{ width: "100%" }}>
                          <Button type="primary" htmlType="submit" block size="large" style={{ background: navy, borderColor: navy }} loading={loading}>
                            Sign In
                          </Button>
                          <Button type="default" block size="large" onClick={() => history.push("/registerUser")}>
                            Create account
                          </Button>
                        </Space>
                      </Form.Item>

                      <p style={{ marginTop: 18, textAlign: "center", color: "#888" }}>
                        &copy; {currentYear} Swimmingly — All rights reserved.
                      </p>
                    </Form>
                  </div>

                  {/* Reset panel */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transition: "transform 350ms cubic-bezier(.2,.8,.2,1)",
                      willChange: "transform",
                      backfaceVisibility: "hidden",
                      transform: authView === "login" ? "translateX(100%)" : "translateX(0%)",
                      padding: 0,
                    }}
                  >
                    <div style={{ position: "relative", marginBottom: 12, height: 40 }}>
                      <Button
                        type="link"
                        icon={<LeftOutlined />}
                        onClick={backToLogin}
                        style={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)" }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          left: "50%",
                          top: "50%",
                          transform: "translate(-50%, -50%)",
                          fontWeight: 700,
                        }}
                      >
                        Reset your password
                      </div>
                    </div>

                    <PasswordReset
                      inline
                      initialEmail={email}
                      onCancel={backToLogin}
                      onSuccess={onResetSuccess}
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        <CookiePermissionModal visible={cookieModalVisible} setVisible={setCookieModalVisible} acceptCookies={acceptCookies} />
      </div>
    </>
  )
}